import React, { useState, useEffect } from "react";
import styles from "../../styles/pages/profile/ProfilePage.module.css";
import api from "../../utils/api/axiosInstance";
import { FaUser, FaCheck, FaLock, FaKey, FaPalette, FaAddressCard } from "react-icons/fa";
import CollapsibleSection from "../common/CollapsibleSection";
import ShoppingIdentitySection from "./ShoppingIdentitySection";
import { useTheme } from "../../contexts/ThemeContext";

const SettingsForm = ({ user, setUser, showToast, logout }) => {
  const { theme, setTheme } = useTheme();
  const [form, setForm] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    username: user?.username || "",
    email: user?.email || "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    reauthCode: "",
    bio: user?.profile?.bio || "",
    location: user?.profile?.location || "",
    expertise: user?.profile?.expertise || [],
    profilePublic: user?.profile?.public !== false, // Default to true
    showEmail: user?.profile?.showEmail || false,
  });

  const [saving, setSaving] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [errors, setErrors] = useState({});
  const oauthOnly = user?.hasPassword === false;

  const sendReauthCode = async () => {
    try {
      setSendingCode(true);
      await api.post("/users/me/reauth-code", { purpose: "credentials" });
      showToast({
        message: "Confirmation code sent to your email",
        type: "success",
      });
    } catch (err) {
      showToast({
        message: err.response?.data?.message || "Could not send a confirmation code",
        type: "error",
      });
    } finally {
      setSendingCode(false);
    }
  };

  // Sync form state with user object when it changes
  useEffect(() => {
    if (user) {
      setForm({
        firstName: user?.firstName || "",
        lastName: user?.lastName || "",
        username: user?.username || "",
        email: user?.email || "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
        reauthCode: "",
        bio: (user?.profile?.bio || "").substring(0, 150),
        location: user?.profile?.location || "",
        expertise: user?.profile?.expertise || [],
        profilePublic: user?.profile?.public !== false, // Default to true
        showEmail: user?.profile?.showEmail || false,
      });
    }
  }, [user]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: undefined, general: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.firstName?.trim()) e.firstName = "First name is required";
    else if (/\s/.test(form.firstName.trim()))
      e.firstName = "No internal spaces allowed";

    if (!form.lastName?.trim()) e.lastName = "Last name is required";
    else if (/\s/.test(form.lastName.trim()))
      e.lastName = "No internal spaces allowed";

    if (!form.username?.trim()) e.username = "Username is required";
    if (!form.email?.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Invalid email";

    const emailChanged =
      form.email.trim().toLowerCase() !==
      String(user?.email || "").trim().toLowerCase();
    const oauthOnly = user?.hasPassword === false;
    const wantsPwChange =
      form.currentPassword || form.newPassword || form.confirmPassword || form.reauthCode;

    if (emailChanged || form.newPassword) {
      if (!oauthOnly && !form.currentPassword) {
        e.currentPassword = "Current password required";
      }
      if (oauthOnly && !String(form.reauthCode || "").trim()) {
        e.reauthCode = "Enter the confirmation code sent to your email";
      }
    }

    if (wantsPwChange && form.newPassword) {
      if (!oauthOnly && !form.currentPassword)
        e.currentPassword = "Current password required";
      if (!form.newPassword) e.newPassword = "New password required";
      else if (form.newPassword.length < 8)
        e.newPassword = "At least 8 characters";
      if (form.newPassword !== form.confirmPassword)
        e.confirmPassword = "Passwords do not match";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSaving(true);

      // Build minimal payload (only changed fields)
      const payload = {};
      ["firstName", "lastName", "username", "email"].forEach((k) => {
        if (form[k] !== user?.[k]) payload[k] = form[k].trim();
      });

      // Profile fields - check for any changes
      const currentProfilePublic =
        user?.profile?.public !== undefined ? user.profile.public : true; // Default to true if undefined
      const currentShowEmail = user?.profile?.showEmail || false;

      if (
        form.bio !== (user?.profile?.bio || "") ||
        form.location !== (user?.profile?.location || "") ||
        form.profilePublic !== currentProfilePublic ||
        form.showEmail !== currentShowEmail
      ) {
        payload.profile = {
          bio: form.bio.trim(),
          location: form.location.trim(),
          expertise: form.expertise,
          public: Boolean(form.profilePublic), // Explicitly convert to boolean
          showEmail: Boolean(form.showEmail), // Explicitly convert to boolean
        };
      }

      const emailChanged =
        form.email.trim().toLowerCase() !==
        String(user?.email || "").trim().toLowerCase();
      if (emailChanged || form.newPassword) {
        if (form.currentPassword) payload.currentPassword = form.currentPassword;
        if (form.reauthCode) payload.reauthCode = form.reauthCode.trim();
      }
      if (form.newPassword) {
        payload.newPassword = form.newPassword;
      }

      // No changes?
      if (Object.keys(payload).length === 0) {
        showToast({ message: "Nothing to update", type: "info" });
        return;
      }

      const { data: updated } = await api.patch("/users/me", payload);

      // Update context immediately so UI reflects new values
      setUser(updated);
      showToast({ message: "Profile updated ✅", type: "success" });

      // Clear password fields
      setForm((p) => ({
        ...p,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
        reauthCode: "",
      }));
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Update failed";
      setErrors((p) => ({ ...p, general: msg }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.settingsContainer}>
      <div className={styles.settingsHeader}>
        <h2 className={styles.sectionTitle}>
          <FaUser
            className={`${styles.sectionIcon} ${styles.iconMarginRightSm}`}
          />
          Account Settings
        </h2>
        <p className={styles.sectionSubtitle}>
          Manage your personal information and preferences
        </p>
      </div>
      <form className={styles.settingsForm} onSubmit={handleSave}>
        <CollapsibleSection
          title="Personal information"
          subtitle="Your name, contact details, and public profile"
          titleIcon={<FaAddressCard className={styles.sectionIcon} />}
          defaultExpanded
          fieldsLayout
        >
          <span>
            <label htmlFor="firstName">First Name</label>
            <input
              name="firstName"
              type="text"
              value={form.firstName}
              onChange={onChange}
              placeholder="First Name"
            />
            {errors.firstName && (
              <div className="errorMessage">{errors.firstName}</div>
            )}
          </span>
          <span>
            <label htmlFor="lastName">Last Name</label>
            <input
              name="lastName"
              type="text"
              value={form.lastName}
              onChange={onChange}
              placeholder="Last Name"
            />
            {errors.lastName && (
              <div className="errorMessage">{errors.lastName}</div>
            )}
          </span>

          <span>
            <label htmlFor="username">Username</label>
            <input
              name="username"
              type="text"
              value={form.username}
              onChange={onChange}
              placeholder="Username"
            />
            {errors.username && (
              <div className="errorMessage">{errors.username}</div>
            )}
          </span>

          <span>
            <label htmlFor="email">Email</label>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              placeholder="Email"
            />
            {errors.email && <div className="errorMessage">{errors.email}</div>}
            <small className={styles.helpText}>
              {oauthOnly
                ? "Changing your email requires a confirmation code sent to your current address."
                : "Changing your email requires your current password."}
            </small>
          </span>

          <span>
            <label htmlFor="bio">Bio</label>
            <textarea
              name="bio"
              value={form.bio}
              onChange={onChange}
              placeholder="Tell others about yourself..."
              rows={3}
              maxLength={150}
            />
            <div className={styles.bioCounter}>
              <span>{form.bio.length}/150 characters</span>
            </div>
            {errors.bio && <div className="errorMessage">{errors.bio}</div>}
          </span>

          <span>
            <label htmlFor="location">Location (Optional)</label>
            <input
              name="location"
              type="text"
              value={form.location}
              onChange={onChange}
              placeholder="e.g., Calgary, AB"
            />
          </span>
        </CollapsibleSection>

        <ShoppingIdentitySection
          showToast={showToast}
          onSaved={(data) => {
            if (data?.needsShoppingProfileOnboarding === false && user) {
              setUser({
                ...user,
                needsShoppingProfileOnboarding: false,
                shoppingIdentity: data?.identity,
              });
            }
          }}
        />

        <CollapsibleSection
          title="Theme"
          subtitle="Choose how Simpary appears on your device"
          titleIcon={<FaPalette className={styles.sectionIcon} />}
          defaultExpanded={false}
          fieldsLayout
        >
          <span>
            <label htmlFor="themePreference">Theme Preference</label>
            <select
              id="themePreference"
              name="themePreference"
              value={theme}
              onChange={(e) => {
                setTheme(e.target.value);
                showToast({
                  message: `Theme set to ${e.target.value}`,
                  type: "success",
                });
              }}
            >
              <option value="system">System (default)</option>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </span>
        </CollapsibleSection>

        <CollapsibleSection
          title="Privacy Settings"
          subtitle="Control who can see your profile information"
          titleIcon={<FaLock className={styles.sectionIcon} />}
          defaultExpanded={false}
          fieldsLayout
        >
          <span>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={form.profilePublic}
                onChange={(e) =>
                  setForm((p) => ({ ...p, profilePublic: e.target.checked }))
                }
                className={styles.checkbox}
              />
              <span>Make my profile public</span>
            </label>
            <small className={styles.helpText}>
              {form.profilePublic
                ? "Your profile can be viewed by anyone"
                : "Your profile is private and only visible to you"}
            </small>
          </span>

          <span>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={form.showEmail}
                onChange={(e) =>
                  setForm((p) => ({ ...p, showEmail: e.target.checked }))
                }
                className={styles.checkbox}
              />
              <span>Show email on profile</span>
            </label>
            <small className={styles.helpText}>
              Allow others to see your email address on your public profile
            </small>
          </span>
        </CollapsibleSection>

        <CollapsibleSection
          title="Change Password"
          subtitle="Leave blank if you don't want to change your password"
          titleIcon={<FaKey className={styles.sectionIcon} />}
          defaultExpanded={false}
          fieldsLayout
        >
          {oauthOnly && (
            <span>
              <label htmlFor="reauthCode">Email confirmation code</label>
              <input
                name="reauthCode"
                type="text"
                value={form.reauthCode || ""}
                onChange={onChange}
                placeholder="Code from your email"
                autoComplete="one-time-code"
              />
              {errors.reauthCode && (
                <div className="errorMessage">{errors.reauthCode}</div>
              )}
              <button
                type="button"
                className="buttonSecondary"
                onClick={sendReauthCode}
                disabled={sendingCode}
              >
                {sendingCode ? "Sending..." : "Email me a code"}
              </button>
            </span>
          )}

          {!oauthOnly && (
          <span>
            <label htmlFor="currentPassword">Current Password</label>
            <input
              name="currentPassword"
              type="password"
              value={form.currentPassword}
              onChange={onChange}
              placeholder="Current password"
            />
            {errors.currentPassword && (
              <div className="errorMessage">{errors.currentPassword}</div>
            )}
          </span>
          )}

          <span>
            <label htmlFor="newPassword">New Password</label>
            <input
              name="newPassword"
              type="password"
              value={form.newPassword}
              onChange={onChange}
              placeholder="New password"
            />
            {errors.newPassword && (
              <div className="errorMessage">{errors.newPassword}</div>
            )}
          </span>

          <span>
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              name="confirmPassword"
              type="password"
              value={form.confirmPassword}
              onChange={onChange}
              placeholder="Confirm new password"
            />
            {errors.confirmPassword && (
              <div className="errorMessage">{errors.confirmPassword}</div>
            )}
          </span>
        </CollapsibleSection>

        <div className={styles.formActions}>
          <button type="submit" className="buttonPrimary" disabled={saving}>
            {saving ? (
              <>
                <span className={styles.spinner}></span> Saving...
              </>
            ) : (
              <>
                <FaCheck /> Save Changes
              </>
            )}
          </button>
          {errors.general && (
            <div className="errorMessage">{errors.general}</div>
          )}
        </div>
      </form>
    </div>
  );
};

export default SettingsForm;
